import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { applyBrandColor, DEFAULT_BRAND_COLOR } from "@/components/BrandTheme";
import { waLink } from "@/components/OrderQrCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, CalendarCheck, ArrowLeft, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const WD = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const inputCls = "mt-1 h-11 bg-zinc-900 border-zinc-800 text-zinc-100 text-base";

function useDays(info) {
  return useMemo(() => {
    if (!info) return [];
    const out = [];
    for (let i = 0; i <= info.advance_days && out.length < 21; i += 1) {
      const d = new Date(); d.setDate(d.getDate() + i);
      const h = (info.business_hours || {})[WD[d.getDay()]];
      if (h?.open && !info.blocked_dates.includes(iso(d))) out.push(d);
    }
    return out;
  }, [info]);
}

export default function PublicBooking() {
  const { slug } = useParams();
  const [info, setInfo] = useState(null);
  const [svc, setSvc] = useState("");
  const [day, setDay] = useState("");
  const [times, setTimes] = useState(null);
  const [time, setTime] = useState("");
  const [f, setF] = useState({ name: "", phone: "", plate: "", email: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const days = useDays(info);

  useEffect(() => { api.get(`/public/booking/${slug}`).then(({ data }) => setInfo(data)).catch(() => setInfo(false)); }, [slug]);
  useEffect(() => {
    applyBrandColor(info?.shop?.brand_color || DEFAULT_BRAND_COLOR);
    if (info?.shop?.name) document.title = `${info.shop.name} — Agendar`;
    return () => applyBrandColor(DEFAULT_BRAND_COLOR);
  }, [info]);
  useEffect(() => {
    setTime(""); setTimes(null);
    if (!svc || !day) { return; }
    api.get(`/public/booking/${slug}/slots`, { params: { date: day, service_id: svc } }).then(({ data }) => setTimes(data.times)).catch(() => setTimes([]));
  }, [svc, day, slug]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post(`/public/booking/${slug}`, { ...f, service_id: svc, date: day, time }); setDone(data); }
    catch (er) { toast.error(formatApiError(er)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: k === "plate" ? e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 7) : e.target.value }));

  if (info === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>;
  if (info === false || !info.enabled) return <div data-testid="booking-disabled" className="min-h-screen flex flex-col gap-3 items-center justify-center p-6 text-center text-zinc-400">Agendamento online indisponível nesta oficina.<Link to={`/o/${slug}`} className="underline">Voltar</Link></div>;
  const pill = (active) => `px-3 py-2 rounded-xl border text-sm ${active ? "font-bold" : "border-zinc-800 bg-zinc-900 text-zinc-300"}`;
  const pillStyle = (active) => (active ? { borderColor: "var(--c-brand)", background: "rgba(var(--c-brand-rgb)/0.15)", color: "var(--c-brand-neon)" } : {});
  return (
    <div className="min-h-screen px-4 py-8">
      <div className="max-w-xl mx-auto space-y-5">
        <Link to={`/o/${slug}`} className="inline-flex items-center gap-1 text-sm text-zinc-400"><ArrowLeft className="h-4 w-4" /> {info.shop.name}</Link>
        <h1 className="font-display text-4xl font-black uppercase text-zinc-100">Agendar serviço</h1>
        {done ? (
          <div data-testid="booking-done" className="rounded-2xl border border-card2-border bg-card2 p-6 space-y-3">
            <CalendarCheck className="h-8 w-8" style={{ color: "var(--c-brand)" }} />
            <div className="text-lg font-bold text-zinc-100">Pedido enviado!</div>
            <p className="text-sm text-zinc-400">{done.service} em {new Date(`${done.date}T12:00:00`).toLocaleDateString("pt-BR")} às {done.time}. A oficina vai confirmar com você{f.email ? " por e-mail" : ""}.</p>
            {info.shop.whatsapp && <a href={waLink(info.shop.whatsapp, `Olá! Acabei de pedir um agendamento (${done.service}) para ${new Date(`${done.date}T12:00:00`).toLocaleDateString("pt-BR")} às ${done.time}. Placa ${f.plate}.`)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-emerald-600 text-white font-bold"><MessageCircle className="h-4 w-4" /> Avisar no WhatsApp</a>}
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="booking-form">
            <section><Label className="text-xs font-mono-caps text-zinc-400">1. Serviço</Label>
              <div className="mt-2 flex flex-wrap gap-2">{info.services.map((s) => <button type="button" key={s.id} data-testid={`booking-svc-${s.id}`} onClick={() => setSvc(s.id)} className={pill(svc === s.id)} style={pillStyle(svc === s.id)}>{s.name} <span className="text-xs opacity-70">· {s.duration} min</span></button>)}</div>
            </section>
            <section><Label className="text-xs font-mono-caps text-zinc-400">2. Dia</Label>
              <div className="mt-2 flex gap-2 overflow-x-auto pb-1">{days.map((d) => { const v = iso(d); return <button type="button" key={v} data-testid={`booking-day-${v}`} onClick={() => setDay(v)} className={`${pill(day === v)} shrink-0 text-center`} style={pillStyle(day === v)}><div className="text-[11px] uppercase">{d.toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "")}</div><div>{d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}</div></button>; })}</div>
            </section>
            {svc && day && <section><Label className="text-xs font-mono-caps text-zinc-400">3. Horário</Label>
              {times === null ? <Loader2 className="h-4 w-4 animate-spin text-zinc-400 mt-2" /> : times.length === 0 ? <p data-testid="booking-no-times" className="text-sm text-zinc-500 mt-2">Sem horários livres neste dia.</p> :
                <div className="mt-2 grid grid-cols-4 sm:grid-cols-6 gap-2">{times.map((t) => <button type="button" key={t} data-testid={`booking-time-${t}`} onClick={() => setTime(t)} className={pill(time === t)} style={pillStyle(time === t)}>{t}</button>)}</div>}
            </section>}
            {time && <section className="space-y-3"><Label className="text-xs font-mono-caps text-zinc-400">4. Seus dados</Label>
              <Input data-testid="booking-name" required placeholder="Nome" value={f.name} onChange={set("name")} className={inputCls} />
              <Input data-testid="booking-phone" required inputMode="tel" placeholder="Telefone com DDD" value={f.phone} onChange={set("phone")} className={inputCls} />
              <Input data-testid="booking-plate" required autoCapitalize="characters" autoCorrect="off" placeholder="Placa (ABC1D23)" value={f.plate} onChange={set("plate")} className={`${inputCls} font-mono uppercase`} />
              <Input data-testid="booking-email" type="email" placeholder="E-mail (opcional, para receber a confirmação)" value={f.email} onChange={set("email")} className={inputCls} />
              <Textarea data-testid="booking-note" placeholder="Conte o que está acontecendo (opcional)" value={f.note} onChange={set("note")} className="bg-zinc-900 border-zinc-800 text-base" />
              <Button data-testid="booking-submit" disabled={busy} className="w-full h-12 rounded-2xl font-bold uppercase" style={{ background: "var(--c-brand)", color: "var(--c-brand-fg)" }}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pedir agendamento"}</Button>
            </section>}
          </form>
        )}
        <p className="text-center text-[11px] font-mono-caps text-zinc-600 tracking-widest pt-4">feito com AutoOficina</p>
      </div>
    </div>
  );
}
